/**
 * Format date to readable string
 * @param {Date|string} date - Date object or ISO string
 * @param {string} format - 'short', 'long', 'time', or 'datetime'
 * @returns {string} - Formatted date string
 */
export const formatDate = (date, format = 'datetime') => {
  const d = new Date(date);
  
  if (isNaN(d.getTime())) return 'Invalid date';
  
  const options = {
    short: { month: 'short', day: 'numeric', year: 'numeric' },
    long: { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' },
    time: { hour: '2-digit', minute: '2-digit' },
    datetime: { 
      month: 'short', 
      day: 'numeric', 
      hour: '2-digit', 
      minute: '2-digit' 
    },
  };
  
  return d.toLocaleString('en-US', options[format] || options.datetime);
};

/**
 * Get relative time string (e.g., "5 minutes ago")
 * @param {Date|string} date - Date object or ISO string
 * @returns {string} - Relative time string
 */
export const getRelativeTime = (date) => {
  if (!date) return '';

  const d = new Date(date);
  if (isNaN(d.getTime())) return '';

  const now = new Date();
  const diffMs = now - d;
  const diffSecs = Math.floor(diffMs / 1000);
  const diffMins = Math.floor(diffSecs / 60);
  const diffHours = Math.floor(diffMins / 60);
  const diffDays = Math.floor(diffHours / 24);

  if (diffSecs < 0) return 'just now';
  if (diffSecs < 60) return `${diffSecs} second${diffSecs !== 1 ? 's' : ''} ago`;
  if (diffMins < 60) return `${diffMins} minute${diffMins !== 1 ? 's' : ''} ago`;
  if (diffHours < 24) return `${diffHours} hour${diffHours !== 1 ? 's' : ''} ago`;
  if (diffDays < 7) return `${diffDays} day${diffDays !== 1 ? 's' : ''} ago`;

  return formatDate(date, 'short');
};

/**
 * Format duration in seconds to readable string
 * @param {number} seconds - Duration in seconds
 * @returns {string} - Formatted duration string
 */
export const formatDuration = (seconds) => {
  if (!seconds || seconds < 0) return '0s';
  
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const secs = seconds % 60;
  
  if (hours > 0) {
    return `${hours}h ${minutes}m`;
  }
  if (minutes > 0) {
    return `${minutes}m ${secs}s`;
  }
  return `${secs}s`;
};

/**
 * Get date range for quick presets
 * @param {string} preset - 'last6h', 'last12h', 'last24h', 'last7d', 'last30d'
 * @returns {object} - Object with startDate and endDate
 */
export const getDateRange = (preset) => {
  const now = new Date();
  const ranges = {
    last6h: 6 * 60 * 60 * 1000,
    last12h: 12 * 60 * 60 * 1000,
    last24h: 24 * 60 * 60 * 1000,
    last7d: 7 * 24 * 60 * 60 * 1000,
    last30d: 30 * 24 * 60 * 60 * 1000,
  };
  
  const duration = ranges[preset] || ranges.last24h;
  const startDate = new Date(now.getTime() - duration);
  
  return {
    startDate,
    endDate: now,
  };
};

/**
 * Check if date is today
 * @param {Date|string} date - Date to check
 * @returns {boolean} - True if date is today
 */
export const isToday = (date) => {
  const d = new Date(date);
  const today = new Date();
  return d.toDateString() === today.toDateString();
};

/**
 * Get time axis format based on date range
 * @param {Date} startDate - Start date
 * @param {Date} endDate - End date
 * @returns {string} - Format string for time axis
 */
export const getTimeAxisFormat = (startDate, endDate) => {
  const diffMs = endDate - startDate;
  const diffHours = diffMs / (1000 * 60 * 60);
  
  if (diffHours <= 24) return 'HH:mm';
  if (diffHours <= 168) return 'MMM DD HH:mm';
  return 'MMM DD';
};

/**
 * Generate time labels for a date range
 * @param {Date} startDate - Start date
 * @param {Date} endDate - End date
 * @param {number} intervals - Number of intervals
 * @returns {Array} - Array of date objects
 */
export const generateTimeLabels = (startDate, endDate, intervals = 10) => {
  const labels = [];
  const diffMs = endDate - startDate;
  const step = diffMs / intervals;
  
  for (let i = 0; i <= intervals; i++) {
    labels.push(new Date(startDate.getTime() + (step * i)));
  }
  
  return labels;
};

